import { useEffect, useMemo, useState } from 'react';
import type { PayrollBreakdown, PayrollForm, PayrollOptions } from '@ip/shared-types';
import { PAYROLL_FORMS } from '@ip/shared-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { PayrollBreakdownTable } from '@/components/employees/EmployeePayrollCard';
import { useHrApi, SessionExpiredError } from '@/services/api';
import { t } from '@/i18n';

/** Options relevant per employment form (everything else is hidden). */
const FORM_OPTIONS: Record<PayrollForm, Array<keyof PayrollOptions>> = {
  uop: ['pit2', 'kup', 'ppk', 'under26'],
  zlecenie: ['pit2', 'sicknessVoluntary', 'kupAutorskie', 'ppk', 'under26'],
  zlecenie_student: [],
  b2b: [],
  powolanie: ['pit2'],
  // No PPK/under26: dzieło has no pension title and the young-person relief
  // does not cover art. 13 pkt 8 income (engine ignores both).
  dzielo: ['pit2', 'kupAutorskie', 'ownEmployer'],
};

/**
 * HR payroll calculator: pick the employment form, enter gross OR net, and
 * get the live statutory breakdown + total employer cost (backend engine,
 * reference: docs/superpowers/research/pl-payroll-2026.md).
 */
export function PayrollCalculator({ nip }: { nip: string }) {
  const { payrollCalc } = useHrApi();
  const [form, setForm] = useState<PayrollForm>('uop');
  const [mode, setMode] = useState<'gross' | 'net'>('gross');
  const [amount, setAmount] = useState('');
  const [pit2, setPit2] = useState(true);
  const [ppk, setPpk] = useState(false);
  const [kup300, setKup300] = useState(false);
  const [under26, setUnder26] = useState(false);
  const [sickness, setSickness] = useState(true);
  const [kupAutorskie, setKupAutorskie] = useState(false);
  const [ownEmployer, setOwnEmployer] = useState(false);

  const [result, setResult] = useState<PayrollBreakdown | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Reset the (possibly stale) result/error the instant form or mode changes,
  // so a gross-computed breakdown is never shown while "From net" is
  // selected (or vice-versa) during the debounce window below. Adjusting
  // state during render — rather than in an effect — avoids painting the
  // stale panel for even one frame (see https://react.dev/link/derived-state).
  const [computedFor, setComputedFor] = useState(`${form}:${mode}`);
  const currentKey = `${form}:${mode}`;
  if (computedFor !== currentKey) {
    setComputedFor(currentKey);
    setResult(null);
    setError(null);
  }

  const visible = FORM_OPTIONS[form];
  const parsed = useMemo(() => {
    const v = Number(amount.replace(',', '.'));
    return amount.trim() !== '' && Number.isFinite(v) && v >= 0 ? Math.round(v * 100) / 100 : null;
  }, [amount]);

  useEffect(() => {
    if (parsed === null) {
      setResult(null);
      setError(amount.trim() === '' ? null : t('hr.payroll.invalidAmount'));
      return;
    }
    const options: PayrollOptions = {};
    if (visible.includes('pit2')) options.pit2 = pit2;
    if (visible.includes('kup')) options.kup = kup300 ? 300 : 250;
    if (visible.includes('ppk')) options.ppk = ppk;
    if (visible.includes('under26')) options.under26 = under26;
    if (visible.includes('sicknessVoluntary')) options.sicknessVoluntary = sickness;
    if (visible.includes('kupAutorskie')) options.kupAutorskie = kupAutorskie;
    if (visible.includes('ownEmployer')) options.ownEmployer = ownEmployer;

    let cancelled = false;
    setBusy(true);
    // Debounced live calculation.
    const timer = setTimeout(() => {
      payrollCalc(nip, {
        form,
        ...(mode === 'gross' ? { gross: parsed } : { net: parsed }),
        options,
      })
        .then((r) => {
          if (!cancelled) { setResult(r); setError(null); }
        })
        .catch((err) => {
          if (cancelled || err instanceof SessionExpiredError) return;
          setResult(null);
          setError((err as Error).message || t('hr.payroll.calcFailed'));
        })
        .finally(() => { if (!cancelled) setBusy(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [nip, form, mode, parsed, pit2, ppk, kup300, under26, sickness, kupAutorskie, ownEmployer, visible, payrollCalc, amount]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{t('hr.payroll.title')}</CardTitle>
          <p className="text-muted-foreground text-sm">{t('hr.payroll.subtitle')}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('hr.payroll.form')}</Label>
            <Select value={form} onValueChange={(v) => setForm(v as PayrollForm)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYROLL_FORMS.map((f) => (
                  <SelectItem key={f} value={f}>{t(`hr.payroll.forms.${f}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={mode}
              onValueChange={(v) => v && setMode(v as 'gross' | 'net')}
            >
              <ToggleGroupItem value="gross">{t('hr.payroll.modeGross')}</ToggleGroupItem>
              <ToggleGroupItem value="net">{t('hr.payroll.modeNet')}</ToggleGroupItem>
            </ToggleGroup>
            <Label htmlFor="payroll-calc-amount" className="sr-only">
              {mode === 'gross' ? t('hr.payroll.amountGross') : t('hr.payroll.amountNet')}
            </Label>
            <Input
              id="payroll-calc-amount"
              inputMode="decimal"
              placeholder={mode === 'gross' ? t('hr.payroll.amountGross') : t('hr.payroll.amountNet')}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="tabular-nums"
            />
          </div>

          {visible.length > 0 && (
            <div className="space-y-2.5">
              <Label>{t('hr.payroll.options')}</Label>
              {visible.includes('pit2') && (
                <OptionSwitch id="opt-pit2" checked={pit2} onChange={setPit2} label={t('hr.payroll.pit2')} />
              )}
              {visible.includes('kup') && (
                <OptionSwitch id="opt-kup300" checked={kup300} onChange={setKup300} label={t('hr.payroll.kup300')} />
              )}
              {visible.includes('sicknessVoluntary') && (
                <OptionSwitch id="opt-sickness" checked={sickness} onChange={setSickness} label={t('hr.payroll.sickness')} />
              )}
              {visible.includes('kupAutorskie') && (
                <OptionSwitch
                  id="opt-kup50"
                  checked={kupAutorskie}
                  onChange={setKupAutorskie}
                  label={form === 'dzielo' ? t('hr.payroll.kupAutorskieDzielo') : t('hr.payroll.kupAutorskie')}
                />
              )}
              {visible.includes('ownEmployer') && (
                <OptionSwitch id="opt-own-employer" checked={ownEmployer} onChange={setOwnEmployer} label={t('hr.payroll.ownEmployer')} />
              )}
              {visible.includes('ppk') && (
                <OptionSwitch id="opt-ppk" checked={ppk} onChange={setPpk} label={t('hr.payroll.ppk')} />
              )}
              {visible.includes('under26') && (
                <OptionSwitch id="opt-under26" checked={under26} onChange={setUnder26} label={t('hr.payroll.under26')} />
              )}
            </div>
          )}

          {form === 'b2b' && (
            <p className="text-muted-foreground text-sm">{t('hr.payroll.b2bNote')}</p>
          )}
          {form === 'dzielo' && (
            <p className="text-muted-foreground text-sm">{t('hr.payroll.dzieloNote')}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {error ? (
            <p className="text-destructive text-sm">{error}</p>
          ) : result ? (
            <PayrollBreakdownTable breakdown={result} />
          ) : busy ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <p className="text-muted-foreground text-sm">{t('hr.payroll.emptyState')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function OptionSwitch({
  id, checked, onChange, label,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
      <Label htmlFor={id} className="font-normal">{label}</Label>
    </div>
  );
}
